import React, {FC, useState} from 'react';
import {useTranslation} from 'react-i18next';

import CooperationModal from 'components/CooperationModal';
import {JsonLd, buildProductJsonLd, PageCta, PageHero, PageShell, PageTitle, Section} from 'components';
import Icon from 'components/v3/Icon';
import {btnPrimaryLg} from 'components/v3/classes';

const GRID_PATHS = [
  'M3.75 6A2.25 2.25 0 016 3.75h2.25A2.25 2.25 0 0110.5 6v2.25a2.25 2.25 0 01-2.25 2.25H6a2.25 2.25 0 01-2.25-2.25V6zM3.75 15.75A2.25 2.25 0 016 13.5h2.25a2.25 2.25 0 012.25 2.25V18a2.25 2.25 0 01-2.25 2.25H6A2.25 2.25 0 013.75 18v-2.25zM13.5 6a2.25 2.25 0 012.25-2.25H18A2.25 2.25 0 0120.25 6v2.25A2.25 2.25 0 0118 10.5h-2.25a2.25 2.25 0 01-2.25-2.25V6zM13.5 15.75a2.25 2.25 0 012.25-2.25H18a2.25 2.25 0 012.25 2.25V18A2.25 2.25 0 0118 20.25h-2.25A2.25 2.25 0 0113.5 18v-2.25z',
];
const DOCUMENT_PATHS = [
  'M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z',
];
const CHART_PIE_PATHS = [
  'M11 3.055A9.001 9.001 0 1020.945 13H11V3.055z',
  'M20.488 9H15V3.512A9.025 9.025 0 0120.488 9z',
];
const BADGE_CHECK_PATHS = [
  'M9 12.75L11.25 15 15 9.75M21 12c0 1.268-.63 2.39-1.593 3.068a3.745 3.745 0 01-1.043 3.296 3.745 3.745 0 01-3.296 1.043A3.745 3.745 0 0112 21c-1.268 0-2.39-.63-3.068-1.593a3.746 3.746 0 01-3.296-1.043 3.746 3.746 0 01-1.043-3.296A3.745 3.745 0 013 12c0-1.268.63-2.39 1.593-3.068a3.746 3.746 0 011.043-3.296 3.746 3.746 0 013.296-1.043A3.746 3.746 0 0112 3c1.268 0 2.39.63 3.068 1.593a3.746 3.746 0 013.296 1.043 3.746 3.746 0 011.043 3.296A3.745 3.745 0 0121 12z',
];

interface Feature {
  descKey: string;
  iconPaths: string[];
  titleKey: string;
}

const renderFeature = (feature: Feature, t: (key: string) => string) => (
  <div className="glass-card rounded-2xl p-6 lg:p-7" key={feature.titleKey}>
    <div className="w-12 h-12 rounded-lg bg-rose-50 flex items-center justify-center mb-5">
      <Icon className="w-6 h-6 text-rose-600" paths={feature.iconPaths} />
    </div>
    <h3 className="text-lg font-semibold text-slate-900 mb-2.5">{t(feature.titleKey)}</h3>
    <p className="text-sm lg:text-base text-slate-500 leading-relaxed">{t(feature.descKey)}</p>
  </div>
);

interface UseCase {
  descKey: string;
  titleKey: string;
}

const renderUseCase = (useCase: UseCase, t: (key: string) => string) => (
  <div className="glass-card rounded-2xl p-6" key={useCase.titleKey}>
    <div className="h-1.5 w-10 rounded-full bg-gradient-to-r from-rose-500 to-pink-500 mb-5" />
    <h3 className="text-base font-semibold text-slate-900 mb-2">{t(useCase.titleKey)}</h3>
    <p className="text-sm text-slate-500 leading-relaxed">{t(useCase.descKey)}</p>
  </div>
);

const DigitalMarketing: FC = () => {
  const {t} = useTranslation('products');
  const [isCooperationOpen, setIsCooperationOpen] = useState(false);

  const features: Feature[] = [
    {
      descKey: 'marketing.features.selection.desc',
      iconPaths: GRID_PATHS,
      titleKey: 'marketing.features.selection.title',
    },
    {
      descKey: 'marketing.features.listing.desc',
      iconPaths: DOCUMENT_PATHS,
      titleKey: 'marketing.features.listing.title',
    },
    {descKey: 'marketing.features.ads.desc', iconPaths: CHART_PIE_PATHS, titleKey: 'marketing.features.ads.title'},
    {descKey: 'marketing.features.roi.desc', iconPaths: BADGE_CHECK_PATHS, titleKey: 'marketing.features.roi.title'},
  ];

  const useCases: UseCase[] = [
    {
      descKey: 'marketing.useCases.independentSiteSeller.desc',
      titleKey: 'marketing.useCases.independentSiteSeller.title',
    },
    {descKey: 'marketing.useCases.platformSeller.desc', titleKey: 'marketing.useCases.platformSeller.title'},
    {descKey: 'marketing.useCases.dtcBrand.desc', titleKey: 'marketing.useCases.dtcBrand.title'},
    {descKey: 'marketing.useCases.marketingTeam.desc', titleKey: 'marketing.useCases.marketingTeam.title'},
  ];

  return (
    <PageShell>
      <PageTitle
        canonicalUrl="https://www.xiangjifanyi.com/home/digital-marketing"
        description={t('marketing.seo.description')}
        ogDescription={t('marketing.seo.ogDescription')}
        title={t('marketing.seo.title')}
      />
      <JsonLd
        data={buildProductJsonLd({
          homeLabel: t('common:nav.homeAria'),
          path: '/digital-marketing',
          productDescription: t('marketing.seo.description'),
          productName: t('marketing.seo.title'),
        })}
      />
      <CooperationModal isOpen={isCooperationOpen} onClose={() => setIsCooperationOpen(false)} />
      <PageHero
        actions={
          <button className={btnPrimaryLg} onClick={() => setIsCooperationOpen(true)} type="button">
            {t('marketing.cta')}
          </button>
        }
        align="center"
        badge={t('marketing.hero.badge')}
        description={
          <>
            {t('marketing.descLine1')}
            <br />
            {t('marketing.descLine2')}
          </>
        }
        title={t('marketing.title')}
        variant="light"
      />
      <Section title={t('marketing.capabilitiesTitle')}>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">{features.map((f) => renderFeature(f, t))}</div>
      </Section>
      <Section bg="muted" title={t('marketing.useCasesTitle')}>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">{useCases.map((u) => renderUseCase(u, t))}</div>
      </Section>
      <PageCta
        actions={
          <button className={btnPrimaryLg} onClick={() => setIsCooperationOpen(true)} type="button">
            {t('marketing.cta')}
          </button>
        }
        title={t('marketing.ctaTitle')}
      />
    </PageShell>
  );
};

export default DigitalMarketing;
